import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { IMAGES } from '../constants/images';
import styles from './SolutionsPage.module.css';
import SEO from '../components/SEO';

const SOL_SEO_TITLE = 'VEU Rebates: Heat Pump, Reverse-Cycle AC, LED Lighting & Commercial Solar | Movement Green Victoria';
const SOL_SEO_DESC  = 'Access Victorian Government rebates on heat pump hot water systems, reverse-cycle air conditioning (up to $7,000), LED lighting and commercial solar under the VEU program. VEU-eligible installs across Victoria.';
const SOL_KEYWORDS  = 'heat pump hot water rebate Victoria, reverse cycle air conditioner rebate VEU, LED lighting upgrade government rebate, commercial solar VEU Victoria, VEU eligible appliances, air conditioner government rebate Melbourne, heat pump VEU Victoria, AC rebate Victoria 2025';

const SOL_FAQ = [
  {
    q: 'How much is the reverse-cycle air conditioner rebate in Victoria?',
    a: 'Under the Victorian Energy Upgrades (VEU) program,* eligible reverse-cycle air conditioner installations can attract a VEU benefit of up to $7,000 depending on the unit size, property type, and current VEEC prices. A minimum co-payment applies. Book a free assessment to confirm your exact benefit amount.',
  },
  {
    q: 'How much is the heat pump hot water rebate in Victoria?',
    a: 'Victorian homeowners replacing an old electric or gas hot water system with a heat pump hot water system can access a significant VEU* benefit — often resulting in greatly reduced upfront cost. A minimum co-payment applies. In addition, heat pumps reduce hot water energy bills by up to 70% compared to traditional systems.',
  },
  {
    q: 'Does LED lighting qualify for VEU rebates in Victoria?',
    a: 'Yes. LED lighting upgrades are among the most accessible VEU-eligible upgrades for Victorian homes and businesses. Replacing halogen or fluorescent fittings with high-efficiency LED lighting qualifies for a VEU benefit under the VEU program* and can reduce lighting energy use by up to 80%.',
  },
  {
    q: 'Can businesses get a rebate for commercial solar under VEU?',
    a: 'Yes. Commercial solar installations are eligible under the Victorian Energy Upgrades (VEU) program* for qualifying Victorian businesses. The VEU benefit applies to the installation of solar PV systems on commercial premises. Note that residential home solar and solar battery storage are not covered under VEU — they fall under separate Victorian Government schemes. Contact us to find out what your business qualifies for.',
  },
  {
    q: 'Can I get multiple VEU upgrades at the same time?',
    a: 'Yes. Your Victorian home may qualify for multiple upgrades under the VEU program* at the same time — for example, a heat pump hot water system, reverse-cycle AC, and LED lighting all in the same visit. Movement Green assesses your entire property and recommends every eligible upgrade to maximise your total VEU benefit.',
  },
  {
    q: 'How long does a VEU installation take?',
    a: 'Most VEU upgrades are completed within a single day. Heat pump hot water and reverse-cycle AC installations typically take 3–6 hours. LED lighting upgrades can be completed in as little as 1–2 hours depending on the number of fittings. As a VEU Accredited Provider, Movement Green handles all scheduling, installation and scheme paperwork.',
  },
];

const SOL_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ItemList',
      '@id':   'https://movementgreen.com.au/solutions#services',
      name:    'Victorian Energy Upgrade Services',
      itemListElement: [
        {
          '@type': 'ListItem', position: 1,
          item: {
            '@type': 'Service',
            name: 'Heat Pump Hot Water System Installation',
            description: 'VEU-rebated heat pump hot water system. Replace gas or electric — reduce hot water bills by up to 70%. Same-day install across Victoria.',
            provider: { '@id': 'https://movementgreen.com.au/#business' },
            areaServed: 'Victoria, Australia',
            offers: { '@type': 'Offer', description: 'Government rebate available under VEU', priceCurrency: 'AUD' },
          },
        },
        {
          '@type': 'ListItem', position: 2,
          item: {
            '@type': 'Service',
            name: 'Reverse-Cycle Air Conditioner Installation Victoria',
            description: 'Government rebate up to $7,000 on reverse-cycle AC installation under the VEU program. Heating and cooling for Victorian homes.',
            provider: { '@id': 'https://movementgreen.com.au/#business' },
            areaServed: 'Victoria, Australia',
            offers: { '@type': 'Offer', description: 'Up to $7,000 VEU rebate', priceCurrency: 'AUD' },
          },
        },
        {
          '@type': 'ListItem', position: 3,
          item: {
            '@type': 'Service',
            name: 'LED Lighting Upgrade Victoria',
            description: 'VEU-eligible LED lighting upgrade — replace halogen or fluorescent fittings. 80% energy saving, same-day install.',
            provider: { '@id': 'https://movementgreen.com.au/#business' },
            areaServed: 'Victoria, Australia',
            offers: { '@type': 'Offer', description: 'Government rebate available under VEU', priceCurrency: 'AUD' },
          },
        },
        {
          '@type': 'ListItem', position: 4,
          item: {
            '@type': 'Service',
            name: 'Commercial Solar Installation Victoria',
            description: 'VEU-eligible commercial solar PV installation for Victorian businesses. Government rebate available under the Victorian Energy Upgrades program.',
            provider: { '@id': 'https://movementgreen.com.au/#business' },
            areaServed: 'Victoria, Australia',
            offers: { '@type': 'Offer', description: 'Government rebate available under VEU', priceCurrency: 'AUD' },
          },
        },
      ],
    },
    {
      '@type': 'FAQPage',
      '@id':   'https://movementgreen.com.au/solutions#faq',
      mainEntity: SOL_FAQ.map(({ q, a }) => ({
        '@type':        'Question',
        name:           q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  ],
};

const CARDS = [
  {
    img: 'solutionHeatpump',
    alt: 'Modern home with heat pump at twilight',
    name: 'Heat Pump Hot Water',
    tag: 'Most Popular',
    price: 'Govt. rebate available',
    desc: 'Replace your old electric or gas hot water system with an energy-efficient heat pump. Reduce hot water bills by up to 70% and access Victorian Government rebates under the VEU program.*',
    features: ['Up to 70% bill savings', 'VEU rebate eligible', 'Quick install', '3yr+ warranty'],
    dark: false,
    featured: true,
  },
  {
    img: 'solutionLighting',
    alt: 'Modern office with precision LED lighting',
    name: 'LED Lighting Upgrade',
    tag: 'Quick Win',
    price: 'Govt. rebate available',
    desc: 'Swap out inefficient halogen or fluorescent fittings for high-quality LED lights. One of the simplest VEU-eligible upgrades — fast installs with immediate savings on your power bill.',
    features: ['80% energy saving', 'VEU rebate eligible', 'Quick install', 'Highly rated'],
    dark: true,
  },
  {
    img: 'solutionHeating',
    alt: 'Reverse cycle air conditioning installation',
    name: 'Reverse-Cycle AC',
    tag: 'Highest Rebate',
    price: 'Up to $7,000 rebate',
    desc: 'Replace old gas heaters or inefficient units with a high-efficiency reverse-cycle air conditioner. One of the highest rebate values available under the Victorian Energy Upgrades program.*',
    features: ['Up to $7,000 rebate', 'Heating & cooling', 'VEU rebate eligible', 'App monitoring'],
    dark: false,
  },
  {
    img: 'solutionSolar',
    alt: 'Commercial solar panel installation on business premises',
    name: 'Commercial Solar',
    tag: 'For Businesses',
    price: 'Govt. rebate available',
    desc: 'Victorian businesses can access government rebates on commercial solar PV installations under the VEU program.* Reduce your operating costs and energy bills with government-backed solar.',
    features: ['VEU rebate eligible', 'Reduce operating costs', 'Commercial-grade install', 'Govt. backed'],
    dark: true,
  },
];

function FaqAccordion({ items }) {
  const [open, setOpen] = useState(null);
  return (
    <div className={styles.faqList}>
      {items.map((item, i) => (
        <div key={i} className={`${styles.faqItem} ${open === i ? styles.faqItemOpen : ''}`}>
          <button
            className={styles.faqQ}
            onClick={() => setOpen(open === i ? null : i)}
            aria-expanded={open === i}
          >
            <span>{item.q}</span>
            <svg className={styles.faqIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className={styles.faqA} aria-hidden={open !== i}>
            <p>{item.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

function Check() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="7" fill="rgba(181,217,106,0.2)"/>
      <path d="M4 7L6.2 9.2L10 5" stroke="#8AB84A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export default function SolutionsPage() {
  return (
    <main className="page-enter">
      <SEO
        title={SOL_SEO_TITLE}
        description={SOL_SEO_DESC}
        keywords={SOL_KEYWORDS}
        schema={SOL_SCHEMA}
      />

      {/* ── HEADER ─────────────────────────────────── */}
      <section className={styles.header}>
        <div className={styles.headerWrap}>
          <div className={styles.headerLeft}>
            <span className="eyebrow">Victorian Energy Upgrades</span>
            <h1 className={`disp-xl ${styles.h1}`}>
              Upgrades That<br/>Pay for<br/><em>Themselves.</em>
            </h1>
          </div>
          <div className={styles.headerRight}>
            <p className="body-lg">
              We install government-rebated energy upgrades across Victorian homes —
              heat pumps, LED lighting, reverse-cycle AC, and more. Less paperwork, more savings.
            </p>
            <div className={styles.headerStats}>
              {[['$7,000','Max AC rebate'],['70%','Hot water bill savings'],['1 Day','Avg. install time']].map(([n,l])=>(
                <div key={n} className={styles.hStat}>
                  <span className={styles.hStatNum}>{n}</span>
                  <span className={styles.hStatLabel}>{l}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FEATURED GRID ──────────────────────────── */}
      <section className={styles.gridSection}>
        <div className={styles.gridWrap}>
          {/* Large featured card — Heat Pump */}
          <div className={styles.featuredCard}>
            <div className={styles.featuredImg}>
              <img src={IMAGES.solutionHeatpump} alt={CARDS[0].alt} className={styles.featuredImgEl}/>
              <div className={styles.featuredImgOverlay}/>
            </div>
            <div className={styles.featuredBody}>
              <div className={styles.featuredTop}>
                <span className={styles.tag}>{CARDS[0].tag}</span>
                <span className={styles.price}>{CARDS[0].price}</span>
              </div>
              <h2 className={`disp-sm ${styles.featuredName}`}>{CARDS[0].name}</h2>
              <p className="body-md">{CARDS[0].desc}</p>
              <div className={styles.featuredFeatures}>
                {CARDS[0].features.map(f => (
                  <div key={f} className={styles.featuredFeature}><Check/>{f}</div>
                ))}
              </div>
              <Link to="/contact" className="btn btn-primary btn-lg">Learn More →</Link>
            </div>
          </div>

          {/* 3-column grid */}
          <div className={styles.cardGrid}>
            {CARDS.slice(1).map(card => (
              <div key={card.name} className={`${styles.card} ${card.dark ? styles.cardDark : styles.cardLight}`}>
                <div className={styles.cardImg}>
                  <img src={IMAGES[card.img]} alt={card.alt} className={styles.cardImgEl}/>
                  <div className={card.dark ? styles.cardImgOverlayDark : styles.cardImgOverlayLight}/>
                  <span className={`${styles.cardTag} ${card.dark ? styles.cardTagDark : ''}`}>{card.tag}</span>
                </div>
                <div className={styles.cardBody}>
                  <div className={styles.cardTop}>
                    <h3 className={`h-lg ${styles.cardName}`}>{card.name}</h3>
                    <span className={styles.cardPrice}>{card.price}</span>
                  </div>
                  <p className={`body-sm ${styles.cardDesc}`}>{card.desc}</p>
                  <div className={styles.cardFeatures}>
                    {card.features.map(f => (
                      <div key={f} className={`${styles.cardFeature} ${card.dark ? styles.cardFeatureDark : ''}`}>
                        <Check/><span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <Link to="/contact" className={`btn btn-sm ${card.dark ? 'btn-ghost-light' : 'btn-outline'} ${styles.cardBtn}`}>
                    Get Quote →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────── */}
      <section className={styles.faqSection}>
        <div className={styles.faqWrap}>
          <div className={styles.faqHead}>
            <span className="eyebrow">Frequently Asked</span>
            <h2 className={`disp-md ${styles.faqTitle}`}>
              VEU Rebate<br/><em>Questions Answered.</em>
            </h2>
            <p className="body-md" style={{ marginTop: 4 }}>
              Everything you need to know about Victorian Government rebates
              on air conditioners, heat pumps, LED lighting and more.
            </p>
          </div>
          <FaqAccordion items={SOL_FAQ} />
        </div>
      </section>

      {/* ── CTA STRIP ──────────────────────────────── */}
      <section className={styles.cta}>
        <div className={styles.ctaWrap}>
          <div className={styles.ctaLeft}>
            <span className="eyebrow">Not sure where to start?</span>
            <h2 className={`disp-md ${styles.ctaTitle}`}>Book a free<br/><em>Energy Assessment.</em></h2>
          </div>
          <div className={styles.ctaRight}>
            <p className="body-md">Our team visits your Victorian home, checks which appliances are eligible for government rebates,<sup>*</sup> and recommends the upgrades that'll save you the most — at no cost to you.</p>
            <Link to="/contact" className="btn btn-primary btn-lg">Book Assessment →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
